'use client';

/**
 * Drawer Component
 *
 * A responsive drawer component that slides in from a specified side.
 * Used primarily for mobile interfaces to show additional content.
 */
import React from 'react';

import { cn } from '@ui/lib/utils';

import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '../../ui/sheet';

export interface DrawerProps {
  /**
   * Trigger element that opens the drawer
   */
  trigger: React.ReactNode;

  /**
   * Content to display inside the drawer
   */
  children: React.ReactNode;

  /**
   * Side from which the drawer appears
   */
  side?: 'top' | 'right' | 'bottom' | 'left';

  /**
   * Additional classes for the drawer content
   */
  className?: string;

  /**
   * Whether the drawer is open
   */
  open?: boolean;

  /**
   * Callback when the drawer open state changes
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Whether to show a grab handle (primarily for bottom drawers)
   * Defaults to true for bottom drawers, false for others
   */
  showHandle?: boolean;

  /**
   * Additional classes for the drawer handle
   */
  handleClassName?: string;

  /**
   * Title for the drawer (required for accessibility)
   * This will be visually hidden but announced to screen readers
   */
  title?: string;
}

/**
 * Drawer component for displaying content in a slide-out panel
 *
 * @example
 * ```tsx
 * <Drawer
 *   trigger={<Button label="Open Drawer" />}
 *   side="bottom"
 * >
 *   <div>Drawer content goes here</div>
 * </Drawer>
 * ```
 */
export const Drawer: React.FC<DrawerProps> = ({
  trigger,
  children,
  side = 'bottom',
  className,
  open,
  onOpenChange,
  showHandle,
  handleClassName,
  title
}) => {
  // Determine if handle should be shown (default true for bottom drawers)
  const shouldShowHandle = showHandle ?? side === 'bottom';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent side={side} className={className}>
        {/* Required for accessibility - visually hidden title for screen readers */}
        {title && <SheetTitle className="sr-only">{title}</SheetTitle>}
        <SheetDescription className="sr-only">Drawer content</SheetDescription>

        {/* Drawer handle for bottom side */}
        {shouldShowHandle && (
          <div className="px-5 pt-2 pb-3 flex flex-col justify-start items-center">
            <div className={cn('w-12 h-1 bg-neutral-300 rounded-xl', handleClassName)} />
          </div>
        )}
        {children}
      </SheetContent>
    </Sheet>
  );
};
